import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, Inject, NgZone, OnDestroy, OnInit, PLATFORM_ID, QueryList, ViewChildren, afterNextRender, computed, inject, signal, } from '@angular/core';
import { CommonModule, isPlatformBrowser, Location } from '@angular/common';
import { SignalsStoreService } from '../shared/signals-store.service';
import { SidebarComponent } from '../shared/sidebar/sidebar.component';
import { ProductsService } from '../product/products.service';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { auditTime, bufferWhen, filter, map, mergeMap, of, scan, skip, startWith, Subject, switchMap, tap, timer } from 'rxjs';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { StockService } from '../stock/stock.service';
import { MEDIUM } from "../../scss/responsive/responsive";
import { HeaderService } from '../shared/header/header.service';
import { ResolutionService } from '../shared/resolution.service';
import { EmptyMessageComponent } from '../shared/empty-message/empty-message.component';
import { ShopService } from './shop.service';
import { MenuService } from '../shared/menu.service';
import { environment } from '../../environments/environment';
import { ProductCardV2Component } from '../shared/product-card-v2/product-card-v2.component';
import { Product, ProductResume } from '../product/product.types';
import { ActivatedRoute, Router } from '@angular/router';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { SidebarService } from '../shared/sidebar/sidebar.service';
import { DataLayerService } from '../shared/data-layer/data-layer.service';
import { formatStringForURL, getCurrentPathFromUrl } from '../shared/common/utils';
import { TypesenseSearchService } from '../shared/typesense-search.service';
import { CanonicalService } from '../shared/canonical.service';

@Component({
  selector: 'app-shop',
  templateUrl: './shop.component.html',
  styleUrl: './shop.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  // The host carries the viewport-tall column now that the closed-market notice sits above the
  // rail and the grid, so the global message's 40px has to be subtracted here rather than on
  // each column — they only know how to fill what the host leaves them.
  host: {
    '[class.has-global-message]': 'hasGlobalMessages()',
  },
  imports: [
    CommonModule,
    SidebarComponent,
    NgbModule,
    MatProgressBarModule,
    EmptyMessageComponent,
    ProductCardV2Component,
    MatFormFieldModule,
    MatChipsModule,
  ]
})
export class ShopComponent implements OnInit, OnDestroy {

  @ViewChildren('subcategory')
  subcategories!: QueryList<ElementRef>;

  @ViewChildren('subcategorySon')
  subcategoriesSon!: QueryList<ElementRef>;

  #productsService = inject(ProductsService);
  #headerService = inject(HeaderService);
  #resolutionService = inject(ResolutionService);
  #menuService = inject(MenuService);
  #shopService = inject(ShopService);
  sidebarService = inject(SidebarService);
  private dataLayerService = inject(DataLayerService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private location = inject(Location);
  #typesenseService = inject(TypesenseSearchService);
  #destroyRef = inject(DestroyRef);
  #canonicalService = inject(CanonicalService);
  #ngZone = inject(NgZone);

  // Scroll throttle state — keeps category-in-viewport detection cheap.
  #lastScrollProcess = 0;
  #scrollScheduled = false;

  // Post-navigation scroll restore state. `#scrollAppliedFor` holds the URL the restore
  // already ran for (keeps it single-shot per navigation) and `#scrollRestoreToken`
  // invalidates an in-flight stabilization loop when a newer one starts.
  #scrollAppliedFor = '';
  #scrollRestoreToken = 0;

  categoryBanner = computed(() => this.#shopService.categoryBanner());

  signalsStore = inject(SignalsStoreService);
  stockService = inject(StockService);

  isSidebarOpen = this.signalsStore.isSidebarOpen;
  hasSession = this.signalsStore.hasSession;
  selectedCategory = this.signalsStore.selectedCategory;

  isCollapseButtonHovered = signal(false);
  lastSubcategory: string | null = null;
  lastCategory: string | null = null;
  productsSignal = computed(() => this.#setUpProductsSignal());
  categoriesSignal: any = computed(() => this.#productsService.productsSignal());
  marketStatus = computed(() => this.signalsStore.marketStatus());
  closedMarket = computed(() => {
    const ms = this.marketStatus();
    return {
      title: ms.bannerTitle || `Our Online Farmer's Market Opens ${ms.openingDay.toUpperCase()} Morning!`,
      legend: ms.bannerContent || `We're busy sourcing next week's availability! Check back ${ms?.openingDay || 'Friday'} to begin shopping.`
    };
  })

  isContentLoading = computed(() => this.stockService.isLoading() || this.#productsService.isLoading());
  isContentLoaded = computed(() => this.stockService.isLoaded() || this.#productsService.isLoaded());

  isMobile = computed(() => this.#resolutionService.isMobile());

  emptyMessage = 'No products were found.';
  hasGlobalMessages = computed(() => !!this.signalsStore.globalMessages().length);

  //#region flows config
  displayCategoryName = signal(environment.config.flows.shop.displayCategoryName);
  //#endregion

  productDetails = signal<Map<number, Product>>(new Map()); // Cache of product details
  #visibleProducts$ = new Subject<number[]>(); // IDs of products that are visible in the viewport

  filters = computed(() => this.signalsStore.getFilters());

  showFiltersTags = computed(() => this.isMobile() && this.sidebarService.filterTags().length);

  showMoreItemTags = signal(false);

  constructor(@Inject(PLATFORM_ID) private platformId: any) {

    toObservable(this.signalsStore.globalFilter)
      .subscribe(() => this.#productsService.filterBySidebarChange());

    toObservable(this.sidebarService.filterTags)
      .subscribe(() => this.#validateScrollTags());

    // Subscribe to search mode changes while in shop.
    // skip(1) ignores the initial signal emission to avoid double-search on first load.
    toObservable(this.signalsStore.searchModeQuery)
      .pipe(skip(1), takeUntilDestroyed(this.#destroyRef))
      .subscribe(query => {
        console.log('[Shop] searchModeQuery changed:', query, 'isInSearchMode:', this.signalsStore.isInSearchMode());
        if (query && this.signalsStore.isInSearchMode()) {
          this.#enterSearchMode(query);
        }
      });

    // Run the post-navigation scroll as soon as there is a grid to scroll, instead of relying
    // only on the sidebar's (outIsMenuReady) — that one is delayed 100ms and never fires for a
    // category without sidebar entries. scrollIntoView() is single-shot per navigation, so
    // whichever trigger arrives first wins and the other one is a no-op.
    toObservable(this.#productsService.productsSignal)
      .pipe(filter(products => !!products.length), takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => this.scrollIntoView());

    afterNextRender(() => {
      const isMobile = window.innerWidth <= MEDIUM;
      if (isMobile) this.signalsStore.isSidebarOpen.set(false);
    });
  }

  ngOnInit(): void {

    this.signalsStore.setIsInShopComponent(true);
    // Client-only: the product DETAIL (getProductsDetailsByIds) carries per-user data (e.g.
    // favorites) and must NOT be fetched during SSR — the SSR HTML is cached and shared across
    // users, so baking per-user detail in would leak/serve wrong data. Fetching it only on the
    // client also removes the OOS→mixed→OOS hydration flash and speeds up SSR (no batch wait).
    // SSR renders the generic resume only (banner/image/name/base price).
    if (isPlatformBrowser(this.platformId)) {
      this.#setupBatchRequests();
    }
    this.#setUpSEOTitle();

    // Browser back from /shop?q=… to /shop drops the q param. Detect that and exit
    // search mode so the user lands back on a normal shop view (and triggers the
    // scroll-position restore wired into exitSearchMode).
    this.activatedRoute.queryParamMap
      .pipe(skip(1), takeUntilDestroyed(this.#destroyRef))
      .subscribe(params => {
        const q = params.get('q');
        if (!q && this.signalsStore.isInSearchMode()) {
          this.exitSearchMode();
        }
      });

    // Check for search mode via query param or signal
    const queryParam = this.activatedRoute.snapshot.queryParamMap.get('q');
    if (queryParam || this.signalsStore.isInSearchMode()) {
      const query = queryParam || this.signalsStore.searchModeQuery();
      if (query) {
        this.#enterSearchMode(query);
        return;
      }
    }

    if (this.signalsStore.filterByProducer()) {
      setTimeout(() => {
        this.#headerService.producerRedirection();
      }, 500);
    }
  }

  ngOnDestroy(): void {
    // Invalidate any in-flight #restoreScroll loop — the grid it was pinning is gone.
    this.#scrollRestoreToken++;
    this.#headerService.clearFilters(true, false);
    this.signalsStore.setIsInShopComponent(false);
    this.signalsStore.isInSearchMode.set(false);
    this.signalsStore.searchModeQuery.set('');
  }

  #setUpSEOTitle() {
    this.activatedRoute.paramMap
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(params => {
        const segments = ['category', 'subcategory', 'slug']
          .map(key => params.get(key))
          .filter((s): s is string => !!s);
        this.#applyShopSEO(segments);
      });
  }

  #applyShopSEO(segments: string[]) {
    const appName = environment.application.name;
    const { baseURL, mainTitle } = environment.config.seo;

    let description = `Shop local organic produce, meat, dairy, and pantry staples — delivered to homes and offices across Philadelphia by ${appName}.`;
    let pageTitle = `Shop | ${appName}`;
    let urlPath = 'shop';
    if (segments.length) {
      const label = segments
        .map(s => s.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()))
        .join(' / ');
      description = `${label} — fresh local produce delivered in Philadelphia by ${appName}.`;
      pageTitle = `${label} | ${appName}`;
      urlPath = `shop/${segments.join('/')}`;
    } else {
      pageTitle = mainTitle;
    }

    this.#canonicalService.setPageTitle(pageTitle);
    this.#canonicalService.setPageUrl(`${baseURL}${urlPath}`);
    this.#canonicalService.setPageDescription(description);
    this.#canonicalService.setPageImage(`${baseURL}assets/images/landing/home/bg-hero-2.webp`);
    this.#canonicalService.setPageType('website');
  }

  isInSearchMode = this.signalsStore.isInSearchMode;
  searchModeQuery = this.signalsStore.searchModeQuery;
  searchResultCount = this.signalsStore.searchResultCount;

  async #enterSearchMode(query: string) {
    this.signalsStore.isInSearchMode.set(true);
    this.signalsStore.searchModeQuery.set(query);
    // Empty the grid first so the user sees a clean loading state (mat-progress-bar) rather
    // than the prior category's products briefly anchored under the new scrollTop.
    this.#productsService.clearProductsAndStock();
    this.#productsService.isLoading.set(true);

    // Always render results from the top, regardless of the user's pre-search scroll
    // position. preSearchScroll already remembers the original spot for exitSearchMode.
    if (isPlatformBrowser(this.platformId)) {
      const el = document.getElementById('layoutShopMain');
      if (el) el.scrollTop = 0;
    }

    // Relevance first, popularity as tiebreaker (position is backend-curated; lower = more popular).
    const result = await this.#typesenseService.search(query, { perPage: 250, sortBy: '_text_match:desc,position:asc' });
    this.searchResultCount.set(result.totalFound);
    this.#productsService.setProductsFromSearch(result.products);
  }

  exitSearchMode() {
    // IMPORTANT: do NOT pre-clear isInSearchMode here. headerService.clearFilters reads it as
    // `wasInSearchMode` to decide whether to reload the category products. If we set it to
    // false first, clearFilters short-circuits and the search results stay on screen.
    this.searchResultCount.set(0);
    this.#headerService.clearFilters(true);

    // Drop the ?q= query param so a refresh doesn't re-enter search mode.
    if (this.activatedRoute.snapshot.queryParamMap.has('q')) {
      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: { q: null },
        queryParamsHandling: 'merge',
      });
    }

    this.signalsStore.restorePreSearchScroll(getCurrentPathFromUrl(this.router.url));
  }

  /**
   * Single owner of the post-navigation scroll for the shop grid. Decides ONCE per navigation
   * between the two possible landings and never lets them compete:
   *
   *  1. the exact spot the user left when they opened a product detail page, and
   *  2. the anchor of the category/subcategory in the URL (deep link / category switch).
   *
   * Both used to run on the same navigation: the product token lived in localStorage and was
   * consumed by whichever of the two callers got there first, so the loser fell through to the
   * anchor branch and dragged the user to the top of the subcategory (ELIO-6725).
   */
  scrollIntoView() {
    if (!isPlatformBrowser(this.platformId)) return;

    // Search mode owns its own scroll (top on enter, restorePreSearchScroll on clear).
    if (this.isInSearchMode()) return;

    // Single-shot per navigation: the sidebar re-emits (outIsMenuReady) every time its menu
    // signal is repopulated — which happens again on the same navigation because the resolver
    // clears and refetches the category resume — and the products signal emits more than once
    // too. Without this guard the later trigger would re-run the anchor branch and undo the
    // restore that already landed.
    //
    // Keyed on the PATH, not the full URL, so the enter/clear search cycle (which only toggles
    // ?q= on the same path and reloads the category products) does not read as a new navigation
    // and hijack the scroll that restorePreSearchScroll is already restoring.
    const navigationKey = getCurrentPathFromUrl(this.router.url);
    if (this.#scrollAppliedFor === navigationKey) return;
    this.#scrollAppliedFor = navigationKey;

    // Returning from a PDP wins over the route anchor: the anchor is only where the
    // subcategory starts, the snapshot is where the user actually was.
    const restore = this.#shopService.consumeGridScroll();
    const legacyProductId = localStorage.getItem('selectedProductId');
    if (legacyProductId) localStorage.removeItem('selectedProductId');

    const productId = restore?.productId || legacyProductId;
    if (productId) {
      // Without a snapshot (PDP opened from the search dropdown or a direct URL, gift-cards
      // flow) there is no saved offset, so keep the legacy "card top - 100px" landing.
      this.#restoreScroll(() => document.getElementById(productId), restore ? restore.delta : -100);
      return;
    }

    this.#handleRouteChanges();
  }

  /**
   * Scrolls #layoutShopMain so `getEl()` sits at `offsetTop + delta`, re-measuring on animation
   * frames until the measurement stops moving.
   *
   * The grid settles asynchronously after a remount: below-the-fold cards are @defer'd and only
   * render as they enter the viewport, banner images reserve no height, and the category resume
   * may still be in flight when we're called. The previous version wrote the scroll five times
   * on a fixed [16,100,250,500,1000]ms ladder — writing while the layout was still moving AND
   * still writing long after it had settled, which is the "jumping around" users reported.
   *
   * Correcting every frame instead is what removes the visible jump: any shift above the target
   * is compensated within the same frame, so the target stays pinned under the viewport instead
   * of sliding and being yanked back. Stability is only honoured after MIN_SETTLE so a late
   * image/banner load above the fold can still be absorbed, and the loop bails out the moment
   * the user takes over the scroll so we never fight their gesture.
   */
  #restoreScroll(getEl: () => HTMLElement | null, delta: number) {
    const TARGET_TIMEOUT = 3000; // grid may still be loading when we're called
    const MIN_SETTLE = 600;      // keep pinning while images/banners above may still load
    const SETTLE_TIMEOUT = 1500; // hard cap once the target is in the DOM
    const STABLE_FRAMES = 2;

    const token = ++this.#scrollRestoreToken;
    const start = performance.now();
    let settleStart = 0;
    let lastOffsetTop = NaN;
    let stableFrames = 0;
    let cancelled = false;

    const userTakeOver = () => cancelled = true;
    // Any deliberate input aborts the restore: wheel/touchmove cover scrolling, mousedown
    // covers a scrollbar drag or a click on a card, keydown covers space/arrow scrolling.
    const takeOverEvents = ['wheel', 'touchmove', 'mousedown', 'keydown'];
    takeOverEvents.forEach(e => document.addEventListener(e, userTakeOver, { passive: true }));
    const cleanUp = () => takeOverEvents.forEach(e => document.removeEventListener(e, userTakeOver));

    const step = () => {
      // A newer restore (or the user) supersedes this loop.
      if (cancelled || token !== this.#scrollRestoreToken) {
        cleanUp();
        return;
      }

      const parent = document.getElementById('layoutShopMain');
      const el = getEl();

      if (!parent || !el) {
        if (performance.now() - start > TARGET_TIMEOUT) cleanUp();
        else requestAnimationFrame(step);
        return;
      }

      if (!settleStart) settleStart = performance.now();

      const offsetTop = el.offsetTop;
      const top = Math.max(0, offsetTop + delta);
      if (Math.abs(parent.scrollTop - top) > 1) parent.scrollTop = top;

      stableFrames = offsetTop === lastOffsetTop ? stableFrames + 1 : 0;
      lastOffsetTop = offsetTop;

      const elapsed = performance.now() - settleStart;
      if ((stableFrames >= STABLE_FRAMES && elapsed >= MIN_SETTLE) || elapsed > SETTLE_TIMEOUT) {
        cleanUp();
        return;
      }

      requestAnimationFrame(step);
    };

    // Outside the Angular zone: this ticks every frame for up to ~1.5s and only touches the
    // DOM, so it must not schedule a change-detection pass per frame.
    this.#ngZone.runOutsideAngular(() => requestAnimationFrame(step));
  }

  toggleSidebar() {
    this.signalsStore.isSidebarOpen.update((currentValue) => !currentValue);
  }

  toggleCollapseBarHover(hovered: boolean) {
    this.isCollapseButtonHovered.set(hovered);
  }

  scrollToTop(): void {
    const scrollToTop = document.getElementById('scroll-to-top-ref');
    if (scrollToTop) {
      scrollToTop.scrollIntoView({ block: 'start' });
    }
  }

  getCategoryBanner(categoryId: number): { banner: string, url: string } | null {

    const category = this.#menuService.menu().find(x => x.id === categoryId);

    const banner = category?.banners?.[this.isMobile() ? 'mobile' : 'desktop'];

    if (!banner) return null;

    return {
      banner,
      url: category.banners.url
    };
  }

  getSubCategoryBanner(categoryId: number, subCategoryId: number): { banner: string, url: string } | null {

    const category = this.#menuService.menu().find(x => x.id === categoryId);

    if (!category) return null;

    const subcategory = category.subCategories
      .find((x: any) => x.id === subCategoryId);

    const banner = subcategory?.banners?.[this.isMobile() ? 'mobile' : 'desktop'];

    if (!banner) return null;

    return {
      banner,
      url: subcategory.banners.url
    };
  }

  #setupBatchRequests() {
    const noActivity$ = this.#visibleProducts$.pipe(
      switchMap(() => timer(100).pipe(map(() => true))), // Si no hay nuevos valores en 500ms → inactividad
      startWith(false) // Al inicio hay actividad
    );

    this.#visibleProducts$.pipe(
      bufferWhen(() => noActivity$.pipe(filter(inactive => inactive))), // Espera a que haya inactividad
      filter(productGroups => productGroups.length > 0), // Evita procesar arrays vacíos
      mergeMap(productGroups => productGroups.flat()), // 🔄 Aplana el array → number[]
      scan((acc: number[], productId: number) => [...new Set([...acc, productId])], []), // 📌 Acumula productos únicos
      auditTime(500), // ⏳ Espera estabilidad antes de hacer la petición
      switchMap(productIds => {
        // console.log("📌 Productos acumulados para fetch:", productIds);
        const idsToFetch = productIds.filter(id => !this.productDetails().has(id));
        // console.log("⚠️ Productos a buscar:", idsToFetch);
        return idsToFetch.length > 0 ? this.#productsService.getProductsDetailsByIds(idsToFetch) : of({});
      })
    ).subscribe(details => {
      this.productDetails.update(prev => {
        // console.log("✅ Detalles recibidos:", details);
        const updatedMap = new Map(prev); // Clonar el Map existente
        Object.entries(details).forEach(([index, detail]) => {
          const productDetail: Product = detail as Product;
          updatedMap.set(Number(productDetail.id), productDetail as Product); // Convertir id a número y agregarlo
        });
        return updatedMap;
      });
    });
  }

  onProductCardReady(productId: number) {
    if (this.productDetails().has(productId)) return;
    else this.#visibleProducts$.next([productId]);
  }

  onProductCardHover(productId: number) {
    if (this.productDetails().has(productId)) return;
    else this.#visibleProducts$.next([productId]);
  }

  removeChipFilter(key: string) {
    if (key && this.filters().has(key)) {
      const type = this.filters().get(key)?.type;
      if (!type) return;

      this.signalsStore.setFilterChipRemoved(key);
    }
  }

  #handleRouteChanges() {
    if (!isPlatformBrowser(this.platformId)) return;
    const category = this.activatedRoute.snapshot.paramMap.get('category');
    const subcategory = this.activatedRoute.snapshot.paramMap.get('subcategory');
    const subcategorySon = this.activatedRoute.snapshot.paramMap.get('slug');
    const routeArray = [];
    if (category) routeArray.push(category);
    if (subcategory) routeArray.push(subcategory);
    if (subcategorySon) routeArray.push(subcategorySon);
    if (!routeArray.length) return;

    const anchorLink = `/shop/${routeArray.join('/')}`;
    this.#restoreScroll(() => document.querySelector<HTMLElement>(`[shop-data-anchor-link="${anchorLink}"]`), 0);
  }

  changeAnchorLink(anchorLink: string) {
    this.location.replaceState(anchorLink);
  }

  onScroll(event: any) {
    // Coalesce scroll events: at most one #processScroll per animation frame,
    // and at most one every 150ms. Runs outside Angular zone so scroll itself
    // does not trigger change detection.
    if (this.#scrollScheduled) return;
    this.#scrollScheduled = true;
    this.#ngZone.runOutsideAngular(() => {
      requestAnimationFrame(() => {
        this.#scrollScheduled = false;
        const now = performance.now();
        if (now - this.#lastScrollProcess < 150) return;
        this.#lastScrollProcess = now;
        this.#processScroll(event);
      });
    });
  }

  #processScroll(event: any) {
    // Nothing products in shop
    if (!this.subcategories?.length)
      return;

    const contentY = document
      .getElementsByClassName('layout-shop__main')[0]
      ?.getBoundingClientRect().y ?? 0;

    if (!contentY)
      return;

    // Combine both arrays with their type identifier
    const allElements = [
      ...this.subcategories.toArray().map(el => ({ el, isSon: false })),
      ...this.subcategoriesSon.toArray().map(el => ({ el, isSon: true }))
    ];

    // Calculate distances for all elements
    const elementsWithDistance = allElements.map(({ el, isSon }) => {
      const diffY = el.nativeElement.getBoundingClientRect().y - contentY;
      return { el, diffY, isSon };
    });

    let element: ElementRef<any> | undefined;
    const isBottom = event.srcElement.scrollHeight - event.srcElement.scrollTop <= event.srcElement.clientHeight + 5;

    if (isBottom) {
      // At bottom: get the last visible element (highest diffY that's still negative or slightly positive)
      const found = elementsWithDistance.reduce((prev, curr) =>
        curr.diffY > prev.diffY ? curr : prev
      );
      element = found?.el;
    }
    else {
      // Find the element closest to the top of viewport (within reasonable range)
      const THRESHOLD = 150; // Simplified single threshold

      // First, try to find an element just below or at the top (diffY between -50 and THRESHOLD)
      let found = elementsWithDistance.find(item => item.diffY >= -50 && item.diffY <= THRESHOLD);

      // If not found, get the last element that has passed the viewport top (highest negative diffY)
      if (!found) {
        const passedElements = elementsWithDistance.filter(item => item.diffY < 0);
        if (passedElements.length > 0) {
          // Get the one with the smallest absolute value (closest to 0, meaning most recent)
          found = passedElements.reduce((prev, curr) =>
            curr.diffY > prev.diffY ? curr : prev
          );
        }
      }

      element = found?.el;
    }

    if (!element)
      return;

    // Get the category and subcategory attributes
    const categoryId = +element.nativeElement.getAttribute('category-id');
    const subCategoryId = +element.nativeElement.getAttribute('sub-category-id');
    const subCategorySonId = +element.nativeElement.getAttribute('sub-category-son-id');

    // If yet not change of category, don't do nothing.
    if (this.signalsStore.categoryInViewPort()?.categoryId === categoryId &&
      this.signalsStore.categoryInViewPort()?.subCategoryId === subCategoryId &&
      this.signalsStore.categoryInViewPort()?.subCategorySonId === subCategorySonId)
      return;

    // Re-enter the Angular zone for the state update. #processScroll runs via
    // runOutsideAngular (scroll perf), so setting categoryInViewPort outside the zone
    // doesn't schedule change detection — the sidebar's active-item highlight (bound to
    // categoryInViewPort) would otherwise update late, only on the next zone event.
    const viewportAnchorLink = element.nativeElement.getAttribute('shop-data-anchor-link');
    this.#ngZone.run(() => {
      this.signalsStore.categoryInViewPort.set({ categoryId, subCategoryId, subCategorySonId });
      if (viewportAnchorLink) this.changeAnchorLink(viewportAnchorLink);
    });
  }

  #setupProductAnchorLinks(product: any) {
    const anchorLinkArray = ['shop'];

    if (product.category.name) {
      anchorLinkArray.push(formatStringForURL(product.category.name));
      const categoryAnchorLink = `/${anchorLinkArray.join('/')}`;
      product.categoryAnchorLink = categoryAnchorLink;
    }

    if (product.subcategory?.name) {
      anchorLinkArray.push(formatStringForURL(product.subcategory.name));
      const subcategoryAnchorLink = `/${anchorLinkArray.join('/')}`;
      product.subcategoryAnchorLink = subcategoryAnchorLink;
    }

    if (product.subcategorySon?.name) {
      anchorLinkArray.push(formatStringForURL(product.subcategorySon.name));
      const subcategorySonAnchorLink = `/${anchorLinkArray.join('/')}`;
      product.subcategorySonAnchorLink = subcategorySonAnchorLink;
    }
  }

  #getCategoryBanner(categoryId: number, subCategoryId?: number, subCategorySonId?: number) {

    let item = this.#menuService.menu()
      .find(x => x.id === categoryId);

    if (subCategoryId)
      item = item?.subCategories.find((x: any) => x.id === subCategoryId);

    if (subCategorySonId)
      item = item?.subCategories.find((x: any) => x.id === subCategorySonId);

    return this.#validateBanner(item);
  }

  #validateBanner(item: any): { banner: string, bannerMobile: string, bannerDesktop: string, url: string, show: boolean } {

    const banners = item?.banners;
    const mobile = banners?.mobile || '';
    const desktop = banners?.desktop || '';

    if (!mobile && !desktop)
      return { banner: '', bannerMobile: '', bannerDesktop: '', url: '', show: true };

    // `banner` (single field) is kept for any consumer still reading it directly.
    // Templates should prefer the picture/source pair via bannerMobile/Desktop.
    const banner = this.isMobile() ? (mobile || desktop) : (desktop || mobile);

    return {
      banner,
      bannerMobile: mobile || desktop,
      bannerDesktop: desktop || mobile,
      url: banners.url,
      show: true
    };
  }

  #setUpProductsSignal() {
    const products = this.#productsService.productsSignal();

    let lastCategory: number;
    let lastSubcategory: number;
    let lastSubcategorySon: number;

    if (!products.length) return [];

    // NOTE: this computed used to also kick off the scroll restore (reading and clearing
    // localStorage.selectedProductId). That made it a side-effecting computed that raced the
    // sidebar's (outIsMenuReady) for the same single-use token — whoever lost the race ran the
    // route-anchor scroll instead and the user ended up at the top of the subcategory
    // (ELIO-6725). The restore now has one owner: scrollIntoView().

    // In search mode, skip category/subcategory banner and anchor link generation
    if (this.isInSearchMode()) {
      return products.map((product: any) => {
        product.subCategorySonInView = null;
        product.categoryInView = null;
        product.subCategoryInView = null;
        return product as ProductResume;
      }) as ProductResume[];
    }

    // In search mode, skip category/subcategory banner and anchor link generation
    if (this.isInSearchMode()) {
      return products.map((product: any) => {
        product.subCategorySonInView = null;
        product.categoryInView = null;
        product.subCategoryInView = null;
        return product as ProductResume;
      }) as ProductResume[];
    }

    return products.map((product: any) => {

      product.subCategorySonInView = null;
      product.categoryInView = null;
      product.subCategoryInView = null;

      let categoryId = 0,
        subCategoryId = 0,
        subCategorySonId = 0;

      if (product.category) {
        categoryId = +product.category.id;
        if (categoryId !== lastCategory) {
          product.categoryInView = this.#getCategoryBanner(categoryId);
          lastCategory = categoryId;
        }
      }

      if (product.subcategory) {

        subCategoryId = +product.subcategory.id;
        if (subCategoryId !== lastSubcategory) {
          product.subCategoryInView = this.#getCategoryBanner(categoryId, subCategoryId);
          lastSubcategory = subCategoryId;
        }
      }

      if (product.subcategorySon) {
        subCategorySonId = +product.subcategorySon.id;
        if (subCategorySonId !== lastSubcategorySon) {
          product.subCategorySonInView = this.#getCategoryBanner(categoryId, subCategoryId, subCategorySonId);
          lastSubcategorySon = subCategorySonId;
        }
      }

      this.#setupProductAnchorLinks(product)

      return product as ProductResume;
    }) as ProductResume[];
  }

  #validateScrollTags() {
    if (!isPlatformBrowser(this.platformId)) return;
    setTimeout(() => {
      const ele = document.getElementById('filterTags');
      if (!ele) return;
      const { clientHeight, scrollHeight } = ele;
      this.showMoreItemTags.set(scrollHeight > clientHeight);
    }, 500);
  }

  onBannerClicked(url: string, banner_id: string) {
    this.dataLayerService.trackBannerClickedEvent({
      banner_id,
      banner_link: url || 'no-link'
    });

    if (!url) return;

    setTimeout(() => window.open(url, '_blank'), 100);
  }
}
